/**
 * feat-302 — every new on-screen string the audience chips, the audience editor
 * and View as put on `/quiz-review` (design NOTES §5), in one place.
 *
 * Same reason as `QUIZ_REVIEW_NAV_COPY`: a literal-text grep for copy that is
 * visibly on screen returns a true zero when the copy is inlined in JSX. Tests
 * assert against these constants. Every hierarchy word goes through `QUIZ_TERMS`;
 * `Saving…` / `Try again` are `AUTO_SAVE_COPY`'s, never retyped.
 */
import { QUIZ_TERMS } from '../../quizTerminology';
import { AUTO_SAVE_COPY } from '../../curriculum/autoSaveCopy';

const T = QUIZ_TERMS;

/** The level an audience is attached to. */
export type AudienceLevel = 'track' | 'lesson';

export const levelWord = (level: AudienceLevel): string =>
  level === 'track' ? T.track.singular : T.lesson.singular;

export const AUDIENCE_COPY = {
  // --- chip ----------------------------------------------------------------------
  everyone: 'Everyone',
  /** Count form when a list is longer than the chip can name (NOTES §4). */
  locationsCount: (n: number) => `${n} ${n === 1 ? 'location' : 'locations'}`,
  rolesCount: (n: number) => `${n} ${n === 1 ? 'role' : 'roles'}`,
  except: (names: string) => `except ${names}`,
  chipAria: (level: AudienceLevel, locations: string, roles: string) =>
    `Who sees this ${levelWord(level)}: locations ${locations}; roles ${roles}. Change`,
  ariaEveryLocation: 'every location',
  ariaEveryRole: 'every role',
  saved: AUTO_SAVE_COPY.saved,

  // --- editor --------------------------------------------------------------------
  title: (level: AudienceLevel) => `Who sees this ${levelWord(level)}`,
  close: 'Cancel',
  locations: 'Locations',
  roles: 'Roles',
  modeEveryone: 'Everyone',
  modeOnly: 'Only',
  modeExcept: 'Except',
  captionLocationsOnly: 'Show only at these locations',
  captionLocationsExcept: 'Hide at these locations',
  captionRolesOnly: 'Show only to these roles',
  captionRolesExcept: 'Hide from these roles',
  findLocation: 'Find a location',
  findRole: 'Find a role',
  multiLocationNote: 'Employees who work at more than one location always pass this.',
  trackNoteLead: `Its ${T.track.singular} also decides:`,
  trackNoteShownTo: 'is shown to',
  trackNoteManyLead: `It also needs one of its ${T.track.plural}:`,
  trackPopoverNote:
    `${T.lesson.plural} in this ${T.track.singular} are hidden from anyone this leaves out, ` +
    'whatever their own setting.',
  previewLabel: 'Who will see it',
  previewInvalid: '—',
  invalidLocations: 'Pick at least one location, or switch Locations back to Everyone.',
  invalidRoles: 'Pick at least one role, or switch Roles back to Everyone.',
  invalidBoth: 'Pick at least one location and one role, or switch them back to Everyone.',
  footer: 'Takes effect on phones right away. No approval step.',
  cancel: 'Cancel',
  save: 'Save',
  saving: AUTO_SAVE_COPY.saving,
  tryAgain: AUTO_SAVE_COPY.tryAgain,
  saveError: (level: AudienceLevel) =>
    `Couldn't save who sees this ${levelWord(level)}. Your choices are still here.`,
  locationsLoadFailed: (current: string) =>
    `Couldn't load this tenant's locations, so this stays as it is (${current}).`,
  rolesLoadFailed: (current: string) =>
    `Couldn't load this tenant's roles, so this stays as it is (${current}).`,
  noRoles: 'This tenant has no roles set up yet.',

  // --- View as ---------------------------------------------------------------------
  viewAs: 'View as',
  anyLocation: 'Any location',
  anyRole: 'Any role',
  viewAsLocationAria: 'View as location',
  viewAsRoleAria: 'View as role',
  anyLocationLower: 'any location',
  anyRoleLower: 'any role',
  viewName: (loc: string | null, role: string | null) =>
    `${loc ?? 'any location'} · ${role ?? 'any role'}`,
  viewingAs: 'Viewing as',
  bannerCounts: (t: number, tt: number, l: number, ll: number) =>
    `Showing ${t} of ${tt} ${T.track.plural} and ${l} of ${ll} ${T.lesson.plural}.`,
  clear: 'Clear',
  bannerSubline:
    'Hidden, scheduled and unapproved items still show here, as they always do on this page.',
  updating: 'Updating…',
  bannerError: (view: string) => `Couldn't load the view for ${view}.`,
  bannerErrorTail: 'Showing everything instead.',
  tocLine: (view: string) => `Viewing as ${view}`,
  contentsFiltered: 'Contents · filtered',
  trackEmptyForView: (view: string) => `No ${T.lesson.plural} in this ${T.track.singular} for ${view}.`,
  emptyTitle: (view: string) => `Nothing here for ${view}`,
  /** Body of the empty card. A null picker drops its clause (NOTES §5). */
  emptyBody: (loc: string | null, role: string | null) => {
    const who =
      loc && role
        ? `an employee at ${loc} with the ${role} role`
        : loc
          ? `an employee at ${loc}`
          : `an employee with the ${role} role`;
    return (
      `No ${T.track.singular} in this curriculum is shown to ${who}. ` +
      `A ${T.lesson.singular} only shows when its own audience and at least one of its ` +
      `${T.track.plural} include them.`
    );
  },
  clearViewAs: 'Clear View as',
  editedOut: (view: string) =>
    `Saved. ${view} no longer sees this. It leaves this view when you change or clear View as.`,
  tocEmpty: 'Nothing for this view.',
} as const;
